import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Chart from "chart.js/auto";
import Select from "react-select";
import "./StatsMeetings.css";
import "./StatsDocuments.css";

const exportCsv = (filename, headers, rows) => {
    const escape = (value) => {
        const text = String(value ?? "");
        return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const csv = [headers, ...rows].map((row) => row.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
    if (date.getUTCFullYear() < 1700) return "Unknown";
    return date.toISOString().slice(0, 10);
};

const formatBytes = (value) => {
    const bytes = Number(value || 0);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 ** 3) return `${(bytes / (1024 ** 2)).toFixed(1)} MB`;
    return `${(bytes / (1024 ** 3)).toFixed(2)} GB`;
};

const optionFrom = (row) => ({
    value: row.code,
    label: row.name || row.description || row.label || row.code,
});

function StatsDocuments({ onClose }) {
    const [statsData, setStatsData] = useState({});
    const [filters, setFilters] = useState({
        from: "",
        to: "",
        lodge: "",
        documentType: "",
        fileType: "",
        eventType: "",
        reviewStatus: "",
    });
    const [options, setOptions] = useState({
        lodges: [],
        documentTypes: [],
        fileTypes: [],
        eventTypes: [],
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const monthlyChartRef = useRef(null);
    const monthlyChartInstance = useRef(null);
    const typeChartRef = useRef(null);
    const typeChartInstance = useRef(null);
    const reviewChartRef = useRef(null);
    const reviewChartInstance = useRef(null);

    useEffect(() => {
        const loadOptions = async () => {
            const [lodges, documentTypes, fileTypes, eventTypes] = await Promise.all([
                axios.get("/api/lodges"),
                axios.get("/api/document-types"),
                axios.get("/api/file-types"),
                axios.get("/api/event-types"),
            ]);
            setOptions({
                lodges: (lodges.data || []).map(optionFrom),
                documentTypes: (documentTypes.data || []).map(optionFrom),
                fileTypes: (fileTypes.data || []).map(optionFrom),
                eventTypes: (eventTypes.data || []).map(optionFrom),
            });
        };
        loadOptions().catch((err) => console.error("Failed to load document stat filters:", err));
    }, []);

    useEffect(() => {
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.lodge, filters.documentType, filters.fileType, filters.eventType, filters.reviewStatus, filters.from, filters.to]);

    const fetchStats = async () => {
        try {
            setIsLoading(true);
            setError("");
            const params = Object.fromEntries(
                Object.entries(filters).filter(([, value]) => String(value || "").trim())
            );
            const { data } = await axios.get("/api/stats/documents", { params });
            setStatsData(data || {});
        } catch (err) {
            console.error("Error fetching document statistics:", err);
            setError("Failed to load document statistics.");
        } finally {
            setIsLoading(false);
        }
    };

    const setFilter = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value || "" }));
    };

    const clearDateRange = () => {
        setFilters((prev) => ({ ...prev, from: "", to: "" }));
    };

    const summary = statsData.summary || {};
    const kpis = [
        ["Documents", summary.total_documents || 0],
        ["Lodges", summary.total_lodges || 0],
        ["Storage", formatBytes(summary.total_size_bytes || 0)],
        ["Need Attention", (statsData.needsAttention || []).length],
        ["Tool Updated", summary.tool_updated || 0],
    ];

    useEffect(() => {
        if (!monthlyChartRef.current || isLoading || error) return;
        if (monthlyChartInstance.current) monthlyChartInstance.current.destroy();

        const rows = statsData.documentsByMonth || [];
        monthlyChartInstance.current = new Chart(monthlyChartRef.current.getContext("2d"), {
            type: "bar",
            data: {
                labels: rows.map((row) => row.month),
                datasets: [{
                    label: "Documents",
                    data: rows.map((row) => Number(row.count || 0)),
                    backgroundColor: "rgba(20, 184, 166, 0.70)",
                    borderColor: "rgba(15, 118, 110, 0.95)",
                    borderWidth: 1,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: { ticks: { maxRotation: 55, minRotation: 35 } },
                    y: { beginAtZero: true, title: { display: true, text: "Documents" } },
                },
                plugins: {
                    legend: { display: false },
                    title: { display: true, text: "Documents Added by Month" },
                },
            },
        });

        return () => monthlyChartInstance.current?.destroy();
    }, [error, isLoading, statsData.documentsByMonth]);

    useEffect(() => {
        if (!typeChartRef.current || isLoading || error) return;
        if (typeChartInstance.current) typeChartInstance.current.destroy();

        const rows = (statsData.documentsByType || []).slice(0, 10);
        typeChartInstance.current = new Chart(typeChartRef.current.getContext("2d"), {
            type: "doughnut",
            data: {
                labels: rows.map((row) => row.label),
                datasets: [{
                    data: rows.map((row) => Number(row.count || 0)),
                    backgroundColor: [
                        "#14b8a6", "#60a5fa", "#fb923c", "#a78bfa", "#f43f5e",
                        "#22c55e", "#64748b", "#facc15", "#38bdf8", "#c084fc",
                    ],
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: "bottom" },
                    title: { display: true, text: "Document Type Mix" },
                },
            },
        });

        return () => typeChartInstance.current?.destroy();
    }, [error, isLoading, statsData.documentsByType]);

    useEffect(() => {
        if (!reviewChartRef.current || isLoading || error) return;
        if (reviewChartInstance.current) reviewChartInstance.current.destroy();

        const rows = statsData.reviewStatus || [];
        reviewChartInstance.current = new Chart(reviewChartRef.current.getContext("2d"), {
            type: "bar",
            data: {
                labels: rows.map((row) => row.label),
                datasets: [{
                    label: "Documents",
                    data: rows.map((row) => Number(row.count || 0)),
                    backgroundColor: "rgba(251, 146, 60, 0.72)",
                    borderColor: "rgba(194, 65, 12, 0.95)",
                    borderWidth: 1,
                }],
            },
            options: {
                indexAxis: "y",
                responsive: true,
                maintainAspectRatio: false,
                scales: { x: { beginAtZero: true } },
                plugins: {
                    legend: { display: false },
                    title: { display: true, text: "Description Review Status" },
                },
            },
        });

        return () => reviewChartInstance.current?.destroy();
    }, [error, isLoading, statsData.reviewStatus]);

    const exportSummary = () => {
        exportCsv(
            "document-statistics-summary.csv",
            ["Metric", "Value"],
            [
                ["Documents", summary.total_documents || 0],
                ["Lodges", summary.total_lodges || 0],
                ["Total Size", formatBytes(summary.total_size_bytes || 0)],
                ["Missing Files", summary.missing_files || 0],
                ["Missing Descriptions", summary.missing_descriptions || 0],
                ["No Access Roles", summary.no_access_roles || 0],
                ["Not Reviewed", summary.not_reviewed || 0],
                ["Tool Updated", summary.tool_updated || 0],
                ["Oldest Date", formatDate(summary.oldest_date)],
                ["Newest Date", formatDate(summary.newest_date)],
            ]
        );
    };

    const exportRankings = () => {
        exportCsv(
            "document-statistics-rankings.csv",
            ["Section", "Rank", "Label", "Count"],
            [
                ...(statsData.documentsByLodge || []).map((row, index) => ["Lodge", index + 1, row.label, row.count]),
                ...(statsData.documentsByType || []).map((row, index) => ["Document Type", index + 1, row.label, row.count]),
                ...(statsData.documentsByEventType || []).map((row, index) => ["Event Type", index + 1, row.label, row.count]),
                ...(statsData.documentsByFileType || []).map((row, index) => ["File Type", index + 1, row.label, row.count]),
                ...(statsData.accessRoles || []).map((row, index) => ["Access Role", index + 1, row.label, row.count]),
            ]
        );
    };

    const exportAttention = () => {
        exportCsv(
            "document-statistics-needs-attention.csv",
            ["Document ID", "Title", "Lodge", "Date", "Document Type", "File Type", "Reasons"],
            (statsData.needsAttention || []).map((row) => [
                row.id,
                row.title,
                row.lodge,
                formatDate(row.date_created),
                row.document_type,
                row.file_type,
                (row.reasons || []).join("; "),
            ])
        );
    };

    const openDocument = (id) => {
        window.open(`/view-documents?id=${encodeURIComponent(id)}`, "_blank", "noopener,noreferrer");
    };

    const reviewOptions = useMemo(() => [
        { value: "", label: "All statuses" },
        { value: "none", label: "Not reviewed" },
        { value: "approved", label: "Approved" },
        { value: "no_extract", label: "No extract" },
        { value: "failed", label: "Failed" },
    ], []);

    return (
        <div className="st-modal-overlay">
            <div className="st-modal-content">
                <header className="st-modal-header">
                    <div>
                        <p className="st-eyebrow">Archive Intelligence</p>
                        <h2>Document Statistics</h2>
                    </div>
                    <button className="st-modal-close-btn" onClick={onClose} aria-label="Close Document Statistics">&times;</button>
                </header>

                <div className="st-modal-body st-dashboard">
                    <aside className="st-filter-panel">
                        <h3>Filters</h3>
                        <label>Start Date</label>
                        <input type="date" value={filters.from} onChange={(event) => setFilter("from", event.target.value)} />
                        <label>End Date</label>
                        <input type="date" value={filters.to} onChange={(event) => setFilter("to", event.target.value)} />
                        <button className="st-secondary-btn" type="button" onClick={clearDateRange}>All Dates</button>
                        <label>Lodge</label>
                        <Select
                            options={[{ value: "", label: "All Lodges" }, ...options.lodges]}
                            value={options.lodges.find((item) => item.value === filters.lodge) || { value: "", label: "All Lodges" }}
                            onChange={(option) => setFilter("lodge", option?.value)}
                            isClearable
                            isSearchable
                        />
                        <label>Document Type</label>
                        <Select
                            options={[{ value: "", label: "All Types" }, ...options.documentTypes]}
                            value={options.documentTypes.find((item) => item.value === filters.documentType) || { value: "", label: "All Types" }}
                            onChange={(option) => setFilter("documentType", option?.value)}
                            isClearable
                            isSearchable
                        />
                        <label>File Type</label>
                        <Select
                            options={[{ value: "", label: "All File Types" }, ...options.fileTypes]}
                            value={options.fileTypes.find((item) => item.value === filters.fileType) || { value: "", label: "All File Types" }}
                            onChange={(option) => setFilter("fileType", option?.value)}
                            isClearable
                            isSearchable
                        />
                        <label>Event Type</label>
                        <Select
                            options={[{ value: "", label: "All Events" }, ...options.eventTypes]}
                            value={options.eventTypes.find((item) => item.value === filters.eventType) || { value: "", label: "All Events" }}
                            onChange={(option) => setFilter("eventType", option?.value)}
                            isClearable
                            isSearchable
                        />
                        <label>Review Status</label>
                        <Select
                            options={reviewOptions}
                            value={reviewOptions.find((item) => item.value === filters.reviewStatus) || reviewOptions[0]}
                            onChange={(option) => setFilter("reviewStatus", option?.value)}
                            isClearable
                        />
                        <button className="st-primary-btn" onClick={fetchStats}>Refresh</button>
                        <button className="st-secondary-btn" onClick={exportSummary}>Export Summary CSV</button>
                        <button className="st-secondary-btn" onClick={exportRankings}>Export Rankings CSV</button>
                        <button className="st-secondary-btn" onClick={exportAttention}>Export Attention CSV</button>
                    </aside>

                    <main className="st-main-panel">
                        {isLoading && <div className="loading-spinner">Loading...</div>}
                        {error && <div className="error-message">{error}</div>}
                        {!isLoading && !error && (
                            <>
                                <section className="st-kpi-grid ds-kpi-grid">
                                    {kpis.map(([label, value]) => (
                                        <div key={label} className="st-kpi">
                                            <strong>{value}</strong>
                                            <span>{label}</span>
                                        </div>
                                    ))}
                                </section>

                                <section className="ds-health-strip">
                                    <div>
                                        <strong>{summary.missing_files || 0}</strong>
                                        <span>missing files</span>
                                    </div>
                                    <div>
                                        <strong>{summary.missing_descriptions || 0}</strong>
                                        <span>missing descriptions</span>
                                    </div>
                                    <div>
                                        <strong>{summary.no_access_roles || 0}</strong>
                                        <span>without roles</span>
                                    </div>
                                    <div>
                                        <strong>{summary.not_reviewed || 0}</strong>
                                        <span>not reviewed</span>
                                    </div>
                                    <div>
                                        <strong>{formatDate(summary.oldest_date) || "n/a"}</strong>
                                        <span>oldest record</span>
                                    </div>
                                </section>

                                <section className="st-grid-two">
                                    <div className="st-panel">
                                        <div className="st-panel-heading">
                                            <h3>Archive Volume</h3>
                                            <span>{(statsData.documentsByMonth || []).length} months</span>
                                        </div>
                                        <div className="st-chart-container">
                                            <canvas ref={monthlyChartRef}></canvas>
                                        </div>
                                    </div>
                                    <div className="st-panel">
                                        <div className="st-panel-heading">
                                            <h3>Type Mix</h3>
                                            <span>top 10 types</span>
                                        </div>
                                        <div className="st-chart-container">
                                            <canvas ref={typeChartRef}></canvas>
                                        </div>
                                    </div>
                                </section>

                                <section className="st-grid-two">
                                    <div className="st-panel">
                                        <div className="st-panel-heading">
                                            <h3>Description Review</h3>
                                            <span>automation coverage</span>
                                        </div>
                                        <div className="st-chart-container ds-review-chart">
                                            <canvas ref={reviewChartRef}></canvas>
                                        </div>
                                    </div>
                                    <div className="st-panel">
                                        <div className="st-panel-heading">
                                            <h3>Access Coverage</h3>
                                            <span>documents by role</span>
                                        </div>
                                        <StatsTable
                                            headers={["Role", "Documents"]}
                                            rows={(statsData.accessRoles || []).map((row) => [row.label, row.count])}
                                            empty="No document role data."
                                        />
                                    </div>
                                </section>

                                <section className="st-grid-two">
                                    <div className="st-panel">
                                        <div className="st-panel-heading">
                                            <h3>Top Lodges</h3>
                                            <span>by document count</span>
                                        </div>
                                        <StatsTable
                                            headers={["#", "Lodge", "Documents"]}
                                            rows={(statsData.documentsByLodge || []).map((row, index) => [index + 1, row.label, row.count])}
                                            empty="No lodge document data."
                                        />
                                    </div>
                                    <div className="st-panel">
                                        <div className="st-panel-heading">
                                            <h3>Event Types</h3>
                                            <span>archive activity mix</span>
                                        </div>
                                        <StatsTable
                                            headers={["#", "Event", "Documents"]}
                                            rows={(statsData.documentsByEventType || []).map((row, index) => [index + 1, row.label, row.count])}
                                            empty="No event type data."
                                        />
                                    </div>
                                </section>

                                <section className="st-grid-two">
                                    <div className="st-panel">
                                        <div className="st-panel-heading">
                                            <h3>Document Types</h3>
                                            <span>classification ranking</span>
                                        </div>
                                        <StatsTable
                                            headers={["#", "Type", "Documents"]}
                                            rows={(statsData.documentsByType || []).map((row, index) => [index + 1, row.label, row.count])}
                                            empty="No document type data."
                                        />
                                    </div>
                                    <div className="st-panel">
                                        <div className="st-panel-heading">
                                            <h3>File Types</h3>
                                            <span>format inventory</span>
                                        </div>
                                        <StatsTable
                                            headers={["#", "Format", "Documents"]}
                                            rows={(statsData.documentsByFileType || []).map((row, index) => [index + 1, row.label, row.count])}
                                            empty="No file type data."
                                        />
                                    </div>
                                </section>

                                <section className="st-panel">
                                    <div className="st-panel-heading">
                                        <h3>Needs Attention</h3>
                                        <div className="st-panel-actions">
                                            <span>{(statsData.needsAttention || []).length} records</span>
                                            <button className="st-mini-btn" onClick={exportAttention}>Export</button>
                                        </div>
                                    </div>
                                    <StatsTable
                                        headers={["Date", "Title", "Lodge", "Reasons", "Update"]}
                                        rows={(statsData.needsAttention || []).map((row) => [
                                            formatDate(row.date_created),
                                            row.title || "Untitled",
                                            row.lodge || "",
                                            (row.reasons || []).join(", "),
                                            <button className="st-mini-btn" onClick={() => openDocument(row.id)}>Open</button>,
                                        ])}
                                        empty="No document records need attention in this filter."
                                    />
                                </section>
                            </>
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
}

function StatsTable({ headers, rows, empty }) {
    return (
        <div className="st-table-wrap">
            <table className="st-stats-table">
                <thead>
                <tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr>
                </thead>
                <tbody>
                {rows.length ? rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}
                    </tr>
                )) : (
                    <tr><td colSpan={headers.length}>{empty}</td></tr>
                )}
                </tbody>
            </table>
        </div>
    );
}

export default StatsDocuments;
